import { type RankingInfo, compareItems, rankItem, rankings } from "@tanstack/match-sorter-utils";
import {
	type ColumnDef,
	type FilterFn,
	type SortingFn,
	type TableMeta,
	sortingFns,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { type Dispatch, type SetStateAction } from "react";

import { type DataGridUser } from "./Props";
import { TableCellValue } from "./TableCellValue";
import { type DataGridColumnDef } from "./useColumnState";
import { type ColumnType } from "./validation/parseColumnType";

export const fuzzyFilter: FilterFn<unknown> = (row, columnId, value, addMeta) => {
	// Rank the item
	const itemRank = rankItem(row.getValue(columnId), value, { threshold: rankings.CONTAINS });

	// Store the ranking info
	addMeta(itemRank);

	// Return if the item should be filtered in/out
	return itemRank.passed;
};

export const fuzzySort: SortingFn<unknown> = (rowA, rowB, columnId) => {
	let dir = 0;

	// Only sort by rank if the column has ranking information
	if (rowA.columnFiltersMeta[columnId]) {
		dir = compareItems(
			rowA.columnFiltersMeta[columnId] as RankingInfo,
			rowB.columnFiltersMeta[columnId] as RankingInfo
		);
	}

	// Provide an alphanumeric fallback for when the item ranks are equal
	return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

type InsertDirection = "left" | "right";

// Give our default column cell renderer editing superpowers!
export const defaultColumn: Partial<ColumnDef<unknown>> = {
	cell: TableCellValue,
};

const getUniqueColumnValue = (
	columns: DataGridColumnDef<unknown, unknown>[],
	fieldName: string,
	value: string
) => {
	let uniqueValue = value;
	let i = 1;
	while (columns.find((column) => column[fieldName] === uniqueValue)) {
		uniqueValue = `${value}${fieldName === "header" ? " " : ""}${i}`;
		i++;
	}
	return uniqueValue;
};

export const inferColumnId = (column: DataGridColumnDef<unknown, unknown>) => {
	if (typeof column.id === "string") {
		return column.id;
	}
	if (typeof column["accessorKey"] === "string") {
		return column["accessorKey"];
	}
	if (typeof column.header === "string") {
		return column.header;
	}
};

const deleteColumn = (old: DataGridColumnDef<unknown, unknown>[], headerID: string) =>
	old.filter((column) => inferColumnId(column) !== headerID);

const insertColumn = (
	old: DataGridColumnDef<unknown, unknown>[],
	headerID: string,
	insertDirection: InsertDirection
) =>
	old.reduce(
		(acc, column, index) => {
			const sourceColumn = old[index];
			const header =
				typeof sourceColumn?.header === "string"
					? getUniqueColumnValue(
							old,
							"header",

							sourceColumn.header
						)
					: sourceColumn?.header;
			const id =
				typeof sourceColumn?.id === "string"
					? getUniqueColumnValue(old, "id", sourceColumn.id)
					: undefined;
			const accessorKey =
				typeof sourceColumn?.["accessorKey"] === "string"
					? getUniqueColumnValue(old, "accessorKey", sourceColumn?.["accessorKey"])
					: undefined;
			const newColumn = {
				fieldType: sourceColumn?.fieldType,
				filterFn: sourceColumn?.filterFn,
				sortingFn: sourceColumn?.sortingFn,
				footer: sourceColumn?.footer,
			};

			if (header) {
				newColumn["header"] = header;
			}
			if (id) {
				newColumn["id"] = id;
			}
			if (accessorKey) {
				newColumn["accessorKey"] = accessorKey;
			}

			if (inferColumnId(column) === headerID) {
				if (insertDirection === "left") {
					acc.push(newColumn as DataGridColumnDef<unknown, unknown>);
				}
				acc.push(column);
				if (insertDirection === "right") {
					acc.push(newColumn as DataGridColumnDef<unknown, unknown>);
				}
			} else {
				acc.push(column);
			}
			return acc;
		},
		[] as DataGridColumnDef<unknown, unknown>[]
	);

// Attempt to coerce the value to the specified field type in a reversible way
// If we are unable to coerce the value, we return the original value
const coerceValue = (value: unknown, fieldType: ColumnType | undefined) => {
	const getDateFromValue = (value: unknown) => {
		if (value instanceof Date) {
			return value;
		}

		if (typeof value === "string") {
			return parseISO(value);
		}

		if (typeof value === "number") {
			return new Date(value);
		}

		// Always maintain the original value if it can't be coerced
		return value;
	};
	const getNumberFromValue = (value: unknown) => {
		const number = Number(value);
		// Always maintain the original value if it can't be coerced
		return isNaN(number) ? value : number;
	};
	const getTextFromValue = (value: unknown) => {
		if (typeof value === "object") {
			return JSON.stringify(value);
		}
		return String(value);
	};
	const getObjectFromValue = (value: unknown) => {
		try {
			return JSON.parse(value as string);
		} catch (e) {
			return value;
		}
	};
	switch (fieldType) {
		case "Auto":
			return value;
		case "Boolean":
			return value;
		// "Count",
		// "Created time",
		case "Currency":
			return getNumberFromValue(value);
		case "Date time":
			return getDateFromValue(value);
		case "Date":
			return getDateFromValue(value);
		// "Duration",
		case "Email":
			return getTextFromValue(value);
		// "Last modified time",
		case "Link to record":
			return getTextFromValue(value);
		case "Multiline text":
			return getTextFromValue(value);
		case "Number":
			return getNumberFromValue(value);
		case "Percent":
			return Number(value);
		case "Person":
			return getObjectFromValue(value);
		case "Progress bar":
			return getNumberFromValue(value);
		case "Reaction":
			return getObjectFromValue(value);
		case "Table":
			return getObjectFromValue(value);
		case "Tag":
			if (typeof value === "string") {
				return [getTextFromValue(value)];
			}

			return getObjectFromValue(value);
		case "Text":
			return getTextFromValue(value);
		case "Time distance to now":
			return getDateFromValue(value);
		case "Time":
			return getDateFromValue(value);
		case "URL":
			return getTextFromValue(value);
		default:
			console.error("DataGridAlphaV3 error: unsupported field type", fieldType);
			return value;
	}
};

const editColumn = (
	old: DataGridColumnDef<unknown, unknown>[],
	headerID: string,
	value: DataGridColumnDef<unknown, unknown> | null,
	onDataValueChange?: (mutate: (data: unknown[]) => void) => void
) =>
	old.map((column): DataGridColumnDef<unknown, unknown> => {
		if (inferColumnId(column) !== headerID) {
			return column;
		}

		if (
			value?.["accessorKey"] !== column["accessorKey"] ||
			column.fieldType !== value?.fieldType
		) {
			setTimeout(() => {
				// Coerce column values data when field type changes
				onDataValueChange?.((old) => {
					if (old === undefined) {
						return old;
					}

					const newAccessorKey = value?.["accessorKey"] ?? headerID;

					for (const row of old) {
						if (typeof row !== "object" || row === null) {
							continue;
						}
						const oldValue = row[headerID];
						delete row[headerID];
						row[newAccessorKey] =
							column.fieldType !== value?.fieldType
								? coerceValue(oldValue, value?.fieldType)
								: oldValue;
					}
				});
			}, 0);
		}

		if (!value) {
			return column;
		}

		const newColumn: DataGridColumnDef<unknown, unknown> = Object.assign({}, column, value);
		return newColumn;
	});

export const getTableMeta = (
	setColumns: Dispatch<SetStateAction<DataGridColumnDef<unknown, unknown>[] | undefined>>,
	onDataValueChange?: (mutate: (data: unknown[]) => void) => void,
	defaultDataValue?: unknown[],
	users?: DataGridUser[],
	currentUser?: DataGridUser,
	documentInteractionMode?: "static" | "interactive"
): TableMeta<unknown> => {
	let updateData: TableMeta<unknown>["updateData"] | undefined = undefined;
	let insertRow: TableMeta<unknown>["insertRow"] | undefined = undefined;
	let moveRowToIndex: TableMeta<unknown>["moveRowToIndex"] | undefined = undefined;
	let updateColumn: TableMeta<unknown>["updateColumn"] | undefined = undefined;
	let updateColumnData: TableMeta<unknown>["updateColumnData"] | undefined = undefined;
	let deleteRow: TableMeta<unknown>["deleteRow"] | undefined = undefined;

	if (onDataValueChange !== undefined) {
		updateData = (rowIndex: number | number[], columnId: string, value: unknown) => {
			onDataValueChange((data) => {
				if (!data) {
					return;
				}
				if (typeof rowIndex === "number") {
					if (!data[rowIndex] || typeof data[rowIndex] !== "object") {
						return;
					}
					(data[rowIndex] as object)[columnId] = value;
				} else {
					rowIndex.forEach((index) => {
						if (!data[index] || typeof data[index] !== "object") {
							return;
						}
						(data[index] as object)[columnId] = value;
					});
				}
			});
		};

		insertRow = (rowIndex: number, rowData: unknown = {}) => {
			onDataValueChange((data) => {
				data.splice(rowIndex, 0, rowData);
			});
		};

		moveRowToIndex = (fromIndex: number, toIndex: number | "end") => {
			onDataValueChange((data) => {
				const row = data[fromIndex];
				toIndex = toIndex === "end" ? data.length - 1 : toIndex;
				if (!row || fromIndex === toIndex || toIndex < 0 || toIndex >= data.length) {
					return;
				}

				data.splice(fromIndex, 1);
				data.splice(toIndex, 0, row);
			});
		};

		updateColumnData = (columnId: string, mutate: (value: unknown) => unknown) => {
			onDataValueChange((data) => {
				if (!data) {
					return;
				}

				data.forEach((row) => {
					if (!row || typeof row !== "object") {
						return;
					}
					(row as object)[columnId] = mutate(row[columnId]);
				});
			});
		};

		deleteRow = (rowIndex: number) => {
			onDataValueChange((data) => {
				if (!data) {
					return;
				}

				data.splice(rowIndex, 1);
			});
		};
	}

	updateColumn = (headerID, value, insertDirection) => {
		setColumns((old) => {
			if (old === undefined) {
				return old;
			}

			let columns: DataGridColumnDef<unknown, unknown>[];
			if (value === null && insertDirection === undefined) {
				columns = deleteColumn(old, headerID);
			} else if (insertDirection !== undefined) {
				columns = insertColumn(old, headerID, insertDirection);
			} else {
				columns = editColumn(old, headerID, value, onDataValueChange);
			}

			return columns;
		});
	};

	return {
		tableEditable: documentInteractionMode === "interactive",
		defaultDataValue,
		updateData,
		users,
		currentUser,
		updateColumn,
		updateColumnData,
		insertRow,
		moveRowToIndex,
		deleteRow,
	};
};
